import React, { useState } from 'react';

const SocialDashboard = ({ user, stats, activities, onAction, onShowMap, isLoading = false }) => {
  const [refreshing, setRefreshing] = useState(false);

  const handleRefresh = async () => {
    setRefreshing(true);
    onAction && onAction('refresh');
    setTimeout(() => setRefreshing(false), 1000);
  };

  const getSafetyScoreColor = (score) => {
    if (score >= 80) return 'text-success';
    if (score >= 60) return 'text-warning';
    return 'text-danger';
  };

  const getSafetyBadgeColor = (level) => {
    switch (level) {
      case 'safe': return 'badge-success';
      case 'caution': return 'badge-warning';
      case 'danger': return 'badge-danger';
      default: return 'badge-secondary';
    }
  };

  if (isLoading) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: '#f5f5f5', padding: '16px' }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto', display: 'flex', flexDirection: 'column', gap: '24px' }}>
          {/* Loading skeletons */}
          <div style={{ height: '128px', backgroundColor: '#e0e0e0', borderRadius: '12px', animation: 'pulse 1.5s ease-in-out infinite' }}></div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '16px' }}>
            {[...Array(4)].map((_, i) => (
              <div key={i} style={{ height: '96px', backgroundColor: '#e0e0e0', borderRadius: '8px', animation: 'pulse 1.5s ease-in-out infinite' }}></div>
            ))}
          </div>
        </div>
        <style>
          {`
            @keyframes pulse {
              0%, 100% { opacity: 1; }
              50% { opacity: 0.5; }
            }
          `}
        </style>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: 'calc(100vh - 72px)',
      backgroundColor: '#f8f9fa',
      paddingBottom: '80px',
      overflow: 'auto',
      WebkitOverflowScrolling: 'touch' // Smooth scrolling on iOS
    }}>
      {/* Refreshing Indicator */}
      {refreshing && (
        <div style={{
          position: 'fixed',
          top: '80px', // Below header
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 50,
          backgroundColor: '#2196F3',
          color: 'white',
          padding: '8px 16px',
          borderRadius: '20px',
          fontSize: '14px',
          fontWeight: '600'
        }}>
          🔄 Refreshing...
        </div>
      )}

      <div style={{ padding: '16px 20px 60px 20px' }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto', display: 'flex', flexDirection: 'column', gap: '24px' }}>
          
          {/* Welcome Header with Map Access */}
          <div style={{
            background: 'linear-gradient(135deg, #2196F3 0%, #FF6B6B 100%)',
            borderRadius: '12px',
            color: 'white',
            padding: '24px',
            position: 'relative',
            overflow: 'hidden'
          }}>
            <div style={{ display: 'flex', flexDirection: window.innerWidth <= 768 ? 'column' : 'row', alignItems: window.innerWidth <= 768 ? 'flex-start' : 'center', justifyContent: 'space-between', gap: '16px' }}>
              <div>
                <h1 style={{ fontSize: window.innerWidth <= 768 ? '24px' : '32px', fontWeight: 'bold', margin: 0 }}>
                  Welcome back, {user?.profile?.displayName || user?.username || 'Explorer'}! ✈️
                </h1>
                <p style={{ color: 'rgba(255,255,255,0.8)', marginTop: '8px', margin: 0 }}>
                  Your travel safety companion since {new Date(user?.createdAt || '2023-01-01').getFullYear()}
                </p>
              </div>
              
              <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                {/* Prominent Map Button */}
                <button
                  onClick={onShowMap}
                  style={{
                    backgroundColor: 'rgba(255,255,255,0.2)',
                    border: '2px solid rgba(255,255,255,0.3)',
                    borderRadius: '12px',
                    color: 'white',
                    padding: '12px 20px',
                    fontSize: '16px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    transition: 'all 0.3s ease',
                    minHeight: '44px'
                  }}
                  onMouseOver={(e) => {
                    e.target.style.backgroundColor = 'rgba(255,255,255,0.3)';
                    e.target.style.transform = 'translateY(-2px)';
                  }}
                  onMouseOut={(e) => {
                    e.target.style.backgroundColor = 'rgba(255,255,255,0.2)';
                    e.target.style.transform = 'translateY(0)';
                  }}
                >
                  🗺️ Explore Map
                </button>
                
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '24px', fontWeight: 'bold', color: stats?.safetyScore >= 80 ? '#4CAF50' : stats?.safetyScore >= 60 ? '#FF9800' : '#F44336' }}>
                    {stats?.safetyScore || 0}%
                  </div>
                  <p style={{ fontSize: '12px', color: 'rgba(255,255,255,0.8)', margin: 0 }}>Safety Score</p>
                </div>
                
                <button
                  onClick={handleRefresh}
                  style={{
                    backgroundColor: 'transparent',
                    border: 'none',
                    color: 'white',
                    cursor: 'pointer',
                    padding: '8px',
                    borderRadius: '50%',
                    minHeight: '44px',
                    minWidth: '44px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                  onMouseOver={(e) => e.target.style.backgroundColor = 'rgba(255,255,255,0.1)'}
                  onMouseOut={(e) => e.target.style.backgroundColor = 'transparent'}
                >
                  🔄
                </button>
              </div>
            </div>
            
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '16px', color: 'rgba(255,255,255,0.9)' }}>
              📍 Currently in {user?.profile?.location?.city || 'Unknown Location'}
              <span style={{
                backgroundColor: 'rgba(76, 175, 80, 0.2)',
                color: '#4CAF50',
                padding: '4px 8px',
                borderRadius: '12px',
                fontSize: '12px',
                fontWeight: '600',
                marginLeft: '8px'
              }}>
                ✅ Safe Zone
              </span>
            </div>
          </div>

          {/* Stats Cards Grid */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '16px' }}>
            <div style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: '24px',
              boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
              transition: 'all 0.3s ease'
            }}
            onMouseOver={(e) => e.target.style.transform = 'translateY(-4px)'}
            onMouseOut={(e) => e.target.style.transform = 'translateY(0)'}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <p style={{ fontSize: '14px', color: '#666', margin: 0 }}>Reviews Written</p>
                  <p style={{ fontSize: '28px', fontWeight: 'bold', color: '#2196F3', margin: '8px 0' }}>
                    {stats?.reviewsWritten || 0}
                  </p>
                </div>
                <div style={{
                  height: '48px',
                  width: '48px',
                  backgroundColor: 'rgba(33, 150, 243, 0.1)',
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '24px'
                }}>
                  ⭐
                </div>
              </div>
              <span style={{
                backgroundColor: '#E3F2FD',
                color: '#2196F3',
                padding: '4px 8px',
                borderRadius: '8px',
                fontSize: '12px',
                fontWeight: '600'
              }}>
                Top Contributor
              </span>
            </div>

            <div style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: '24px',
              boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
              transition: 'all 0.3s ease'
            }}
            onMouseOver={(e) => e.target.style.transform = 'translateY(-4px)'}
            onMouseOut={(e) => e.target.style.transform = 'translateY(0)'}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <p style={{ fontSize: '14px', color: '#666', margin: 0 }}>Places Visited</p>
                  <p style={{ fontSize: '28px', fontWeight: 'bold', color: '#FF6B6B', margin: '8px 0' }}>
                    {stats?.placesVisited || 0}
                  </p>
                </div>
                <div style={{
                  height: '48px',
                  width: '48px',
                  backgroundColor: 'rgba(255, 107, 107, 0.1)',
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '24px'
                }}>
                  📍
                </div>
              </div>
            </div>

            <div style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: '24px',
              boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
              transition: 'all 0.3s ease'
            }}
            onMouseOver={(e) => e.target.style.transform = 'translateY(-4px)'}
            onMouseOut={(e) => e.target.style.transform = 'translateY(0)'}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <p style={{ fontSize: '14px', color: '#666', margin: 0 }}>Safety Score</p>
                  <p style={{ fontSize: '28px', fontWeight: 'bold', color: stats?.safetyScore >= 80 ? '#4CAF50' : '#FF9800', margin: '8px 0' }}>
                    {stats?.safetyScore || 0}%
                  </p>
                </div>
                <div style={{
                  height: '48px',
                  width: '48px',
                  backgroundColor: 'rgba(76, 175, 80, 0.1)',
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '24px'
                }}>
                  🛡️
                </div>
              </div>
              <div style={{
                width: '100%',
                height: '6px',
                backgroundColor: '#E0E0E0',
                borderRadius: '3px',
                marginTop: '12px',
                overflow: 'hidden'
              }}>
                <div style={{
                  width: `${stats?.safetyScore || 0}%`,
                  height: '100%',
                  backgroundColor: stats?.safetyScore >= 80 ? '#4CAF50' : '#FF9800',
                  transition: 'width 1s ease'
                }}></div>
              </div>
            </div>

            <div style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: '24px',
              boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
              transition: 'all 0.3s ease'
            }}
            onMouseOver={(e) => e.target.style.transform = 'translateY(-4px)'}
            onMouseOut={(e) => e.target.style.transform = 'translateY(0)'}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <p style={{ fontSize: '14px', color: '#666', margin: 0 }}>Community Impact</p>
                  <p style={{ fontSize: '28px', fontWeight: 'bold', color: '#9C27B0', margin: '8px 0' }}>
                    {stats?.communityImpact || 0}
                  </p>
                </div>
                <div style={{
                  height: '48px',
                  width: '48px',
                  backgroundColor: 'rgba(156, 39, 176, 0.1)',
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '24px'
                }}>
                  💜
                </div>
              </div>
            </div>
          </div>

          {/* Main Content Grid */}
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: window.innerWidth <= 1024 ? '1fr' : '2fr 1fr', 
            gap: '24px' 
          }}>
            {/* Recent Activity Feed */}
            <div style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: '24px',
              boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
            }}>
              <h3 style={{ margin: '0 0 16px 0', fontSize: '20px', color: '#333', display: 'flex', alignItems: 'center', gap: '8px' }}>
                📈 Recent Activity
              </h3>
              
              <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                {activities && activities.length > 0 ? activities.map((activity) => (
                  <div key={activity.id} style={{ 
                    display: 'flex', 
                    alignItems: 'flex-start', 
                    gap: '12px', 
                    padding: '16px', 
                    borderRadius: '8px', 
                    marginBottom: '8px',
                    transition: 'background-color 0.2s ease',
                    cursor: 'pointer'
                  }}
                  onMouseOver={(e) => e.target.style.backgroundColor = '#f5f5f5'}
                  onMouseOut={(e) => e.target.style.backgroundColor = 'transparent'}
                  >
                    <div style={{
                      height: '32px',
                      width: '32px',
                      backgroundColor: '#E3F2FD',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                      fontSize: '16px'
                    }}>
                      {activity.type === 'review' && '⭐'}
                      {activity.type === 'alert' && '⚠️'}
                      {activity.type === 'interaction' && '💬'}
                      {activity.type === 'checkin' && '📍'}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <p style={{ fontWeight: '600', fontSize: '14px', margin: '0 0 4px 0', color: '#333' }}>
                        {activity.title}
                      </p>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', color: '#666', flexWrap: 'wrap' }}>
                        <span>📍 {activity.location}</span>
                        {activity.rating && (
                          <span style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
                            ⭐ {activity.rating}
                          </span>
                        )}
                        {activity.safetyLevel && (
                          <span style={{
                            padding: '2px 6px',
                            borderRadius: '8px',
                            fontSize: '10px',
                            fontWeight: '600',
                            backgroundColor: activity.safetyLevel === 'safe' ? '#E8F5E8' : activity.safetyLevel === 'caution' ? '#FFF3E0' : '#FFEBEE',
                            color: activity.safetyLevel === 'safe' ? '#4CAF50' : activity.safetyLevel === 'caution' ? '#FF9800' : '#F44336'
                          }}>
                            {activity.safetyLevel}
                          </span>
                        )}
                      </div>
                      <p style={{ fontSize: '11px', color: '#999', margin: '4px 0 0 0' }}>
                        {activity.timestamp}
                      </p>
                    </div>
                  </div>
                )) : (
                  <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                    <div style={{ fontSize: '48px', marginBottom: '16px' }}>🌍</div>
                    <p>No recent activity yet. Start exploring to see your journey!</p>
                  </div>
                )}
              </div>
            </div>

            {/* Right Sidebar */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
              {/* Quick Actions Panel */}
              <div style={{
                backgroundColor: 'white',
                borderRadius: '12px',
                padding: '24px',
                boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
              }}>
                <h3 style={{ margin: '0 0 16px 0', fontSize: '18px', color: '#333' }}>
                  Quick Actions
                </h3>
                
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <button
                    onClick={() => onAction && onAction('add-review')}
                    style={{
                      backgroundColor: '#2196F3',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      padding: '12px 16px',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      justifyContent: 'flex-start',
                      transition: 'all 0.2s ease',
                      minHeight: '44px'
                    }}
                    onMouseOver={(e) => e.target.style.backgroundColor = '#1976D2'}
                    onMouseOut={(e) => e.target.style.backgroundColor = '#2196F3'}
                  >
                    ⭐ Add Safety Review
                  </button>
                  
                  <button
                    onClick={() => onAction && onAction('search-locations')}
                    style={{
                      backgroundColor: '#FF6B6B',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      padding: '12px 16px',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      justifyContent: 'flex-start',
                      transition: 'all 0.2s ease',
                      minHeight: '44px'
                    }}
                    onMouseOver={(e) => e.target.style.backgroundColor = '#F44336'}
                    onMouseOut={(e) => e.target.style.backgroundColor = '#FF6B6B'}
                  >
                    🔍 Search Safe Locations
                  </button>
                  
                  <button
                    onClick={() => onAction && onAction('plan-route')}
                    style={{
                      backgroundColor: '#9C27B0',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      padding: '12px 16px',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      justifyContent: 'flex-start',
                      transition: 'all 0.2s ease',
                      minHeight: '44px'
                    }}
                    onMouseOver={(e) => e.target.style.backgroundColor = '#7B1FA2'}
                    onMouseOut={(e) => e.target.style.backgroundColor = '#9C27B0'}
                  >
                    🗺️ Plan Trip Route
                  </button>
                  
                  <button
                    onClick={() => onAction && onAction('emergency-contacts')}
                    style={{
                      backgroundColor: '#F44336',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      padding: '12px 16px',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      justifyContent: 'flex-start',
                      transition: 'all 0.2s ease',
                      minHeight: '44px'
                    }}
                    onMouseOver={(e) => e.target.style.backgroundColor = '#D32F2F'}
                    onMouseOut={(e) => e.target.style.backgroundColor = '#F44336'}
                  >
                    📞 Emergency Contacts
                  </button>
                </div>
              </div>

              {/* Safety Insights */}
              <div style={{
                backgroundColor: 'white',
                borderRadius: '12px',
                padding: '24px',
                boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
              }}>
                <h3 style={{ margin: '0 0 16px 0', fontSize: '18px', color: '#333', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  🛡️ Safety Insights
                </h3>
                
                <div style={{ marginBottom: '16px' }}>
                  <p style={{ fontSize: '14px', fontWeight: '600', margin: '0 0 8px 0' }}>Verification Progress</p>
                  <div style={{
                    width: '100%',
                    height: '8px',
                    backgroundColor: '#E0E0E0',
                    borderRadius: '4px',
                    overflow: 'hidden'
                  }}>
                    <div style={{
                      width: '75%',
                      height: '100%',
                      backgroundColor: '#4CAF50',
                      transition: 'width 1s ease'
                    }}></div>
                  </div>
                  <p style={{ fontSize: '12px', color: '#666', margin: '4px 0 0 0' }}>3 of 4 steps completed</p>
                </div>
                
                <div>
                  <p style={{ fontSize: '14px', fontWeight: '600', margin: '0 0 8px 0' }}>Recommended Safe Areas</p>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    {['Downtown District', 'Tourist Quarter', 'Business Center'].map((area) => (
                      <div key={area} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px' }}>
                        <span style={{ color: '#4CAF50' }}>✅</span>
                        <span>{area}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Community Highlights */}
              <div style={{
                backgroundColor: 'white',
                borderRadius: '12px',
                padding: '24px',
                boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
              }}>
                <h3 style={{ margin: '0 0 16px 0', fontSize: '18px', color: '#333', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  👥 Community Highlights
                </h3>
                
                <div style={{ marginBottom: '16px' }}>
                  <p style={{ fontSize: '14px', fontWeight: '600', margin: '0 0 8px 0' }}>Trending Destinations</p>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    {['Bali, Indonesia', 'Tokyo, Japan', 'Barcelona, Spain'].map((dest) => (
                      <div key={dest} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '13px' }}>
                        <span>{dest}</span>
                        <span style={{
                          backgroundColor: '#FFE0E0',
                          color: '#F44336',
                          padding: '2px 6px',
                          borderRadius: '6px',
                          fontSize: '10px',
                          fontWeight: '600'
                        }}>
                          Hot
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
                
                <div>
                  <p style={{ fontSize: '14px', fontWeight: '600', margin: '0 0 8px 0' }}>Safety Alerts</p>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '6px', fontSize: '12px' }}>
                      <span style={{ color: '#FF9800' }}>⚠️</span>
                      <span>Weather advisory for coastal areas</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '6px', fontSize: '12px' }}>
                      <span style={{ color: '#2196F3' }}>👁️</span>
                      <span>New safety report available for your saved locations</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SocialDashboard;